import React, { useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useForm, SubmitHandler } from 'react-hook-form';
import { validate } from 'isemail';
import { TextFieldWithController } from './TextField';
import { EmailFieldWithController } from './EmailField';
import {
  FileUploadWithController,
  FileUploadHelpText,
  FileUploadLabel,
} from './FileUpload';
import { CheckboxWithController } from './Checkbox';
import { JobApplyFormControlLabel } from './JobApply';
import Button from './Button';
import ValidationError from './ValidationError';
import { getRequiredValidation } from './lib/validationRules';

const accept = '.pdf,application/pdf,.txt,text/plain,.doc,application/msword,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.rtf,application/rtf,.odt,application/vnd.oasis.opendocument.text,.jpg,.jpeg,image/jpeg,.png,image/png';

const jobApplyFormPropertyTypes = {
  defaultValues: PropTypes.shape({}),
  submitHandler: PropTypes.func.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  graphqlError: PropTypes.shape({
    message: PropTypes.string,
  }),
  firstNameLabel: PropTypes.string.isRequired,
  lastNameLabel: PropTypes.string.isRequired,
  emailAddressLabel: PropTypes.string.isRequired,
  cvLabel: PropTypes.string.isRequired,
  motivationLabel: PropTypes.string.isRequired,
  coverLetterLabel: PropTypes.string.isRequired,
  motivationHelpText: PropTypes.string.isRequired,
  extraDocument1Label: PropTypes.string.isRequired,
  extraDocument2Label: PropTypes.string.isRequired,
  subscribedToNewsletterLabel: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  noFileSelectedLabel: PropTypes.string.isRequired,
  allowedFileFormatsLabel: PropTypes.string.isRequired,
  requiredValidationMessage: PropTypes.string.isRequired,
  emailValidationMessage: PropTypes.string.isRequired,
  fileIsEmptyMessage: PropTypes.string.isRequired,
  fileMaxSizeExceededMessage: PropTypes.string.isRequired,
  fileHasInvalidTypeMessage: PropTypes.string.isRequired,
  formatEmailSuggestion: PropTypes.func.isRequired,
  uploadProgress: PropTypes.node,
};

type JobApplyFormProperties = InferProps<
typeof jobApplyFormPropertyTypes
> & {
  submitHandler: SubmitHandler<{
    jobId: number;
    locale: string;
    firstName: string;
    lastName: string;
    email: string;
    subscribedToNewsletter: boolean;
    motivation?: string;
    coverLetter: File | null;
    cv: File;
    extraDocument1: File | null;
    extraDocument2: File | null;
  }>;
  defaultValues: {
    firstName: string;
    lastName: string;
    email: string;
    subscribedToNewsletter: boolean;
    motivation?: string;
    coverLetter?: File | null;
    cv?: File | null;
    extraDocument1?: File | null;
    extraDocument2?: File | null;
  };
};

const JobApplyForm = ({
  defaultValues,
  submitHandler,
  submitDisabled,
  graphqlError,
  firstNameLabel,
  lastNameLabel,
  emailAddressLabel,
  cvLabel,
  motivationLabel,
  coverLetterLabel,
  motivationHelpText,
  extraDocument1Label,
  extraDocument2Label,
  subscribedToNewsletterLabel,
  submitLabel,
  noFileSelectedLabel,
  allowedFileFormatsLabel,
  requiredValidationMessage,
  emailValidationMessage,
  fileIsEmptyMessage,
  fileMaxSizeExceededMessage,
  fileHasInvalidTypeMessage,
  formatEmailSuggestion,
  uploadProgress,
}: JobApplyFormProperties) => {
  const {
    handleSubmit, errors, control,
  } = useForm({
    defaultValues,
  });

  const validateFile = useCallback((value?: {
    name: string;
    size: number;
  }) => {
    if (!value) {
      return true;
    }

    if (value.size === 0) {
      return fileIsEmptyMessage;
    }

    if (value.size > 7_340_032) {
      return fileMaxSizeExceededMessage;
    }

    if (!/\.(pdf|txt|doc|docx|rtf|odt|jpg|jpeg|png)$/i.test(value.name)) {
      return fileHasInvalidTypeMessage;
    }

    return true;
  }, [
    fileIsEmptyMessage,
    fileMaxSizeExceededMessage,
    fileHasInvalidTypeMessage,
  ]);

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <div className="form-item">
        <TextFieldWithController
          name="firstName"
          control={control}
          rules={getRequiredValidation(requiredValidationMessage)}
          defaultValue=""
          id="job-apply-first-name"
          variant="filled"
          type="text"
          label={`${firstNameLabel} *`}
        />
        <ValidationError errors={errors} name="firstName" />
      </div>

      <div className="form-item">
        <TextFieldWithController
          name="lastName"
          control={control}
          rules={getRequiredValidation(requiredValidationMessage)}
          defaultValue=""
          id="job-apply-last-name"
          variant="filled"
          type="text"
          label={`${lastNameLabel} *`}
        />
        <ValidationError errors={errors} name="lastName" />
      </div>

      <div className="form-item">
        <EmailFieldWithController
          name="email"
          control={control}
          rules={{
            validate: (value: string) => (value
              ? validate(value) || emailValidationMessage
              : requiredValidationMessage
            ),
          }}
          variant="filled"
          defaultValue=""
          id="job-apply-email"
          label={`${emailAddressLabel} *`}
          formatSuggestion={formatEmailSuggestion}
          useStringOnClick
        />
        <ValidationError errors={errors} name="email" />
      </div>

      <div className="form-item">
        <FileUploadWithController
          label={`${cvLabel} *`}
          ariaLabel={`${cvLabel} *`}
          control={control}
          rules={{
            required: requiredValidationMessage,
            validate: validateFile,
          }}
          noFileSelectedLabel={noFileSelectedLabel}
          helpText={allowedFileFormatsLabel}
          htmlId="application-cv"
          name="cv"
          accept={accept}
        />
        <ValidationError errors={errors} name="cv" />
      </div>

      <FileUploadLabel>{motivationLabel}</FileUploadLabel>
      <FileUploadHelpText>
        {motivationHelpText}
      </FileUploadHelpText>

      <div className="form-item">
        <TextFieldWithController
          name="motivation"
          control={control}
          rules={{}}
          defaultValue=""
          id="job-apply-motivation"
          variant="filled"
          fullWidth
          multiline
          rows={5}
          type="text"
          label={motivationLabel}
        />
      </div>

      <div className="form-item">
        <FileUploadWithController
          label={coverLetterLabel}
          ariaLabel={coverLetterLabel}
          control={control}
          rules={{ validate: validateFile }}
          noFileSelectedLabel={noFileSelectedLabel}
          helpText={allowedFileFormatsLabel}
          htmlId="application-cover-letter"
          name="coverLetter"
          accept={accept}
        />
        <ValidationError errors={errors} name="coverLetter" />
      </div>

      <div className="form-item">
        <FileUploadWithController
          label={extraDocument1Label}
          ariaLabel={extraDocument1Label}
          control={control}
          rules={{ validate: validateFile }}
          noFileSelectedLabel={noFileSelectedLabel}
          helpText={allowedFileFormatsLabel}
          htmlId="extraDocument1"
          name="extraDocument1"
          accept={accept}
        />
        <ValidationError errors={errors} name="extraDocument1" />
      </div>

      <div className="form-item">
        <FileUploadWithController
          label={extraDocument2Label}
          ariaLabel={extraDocument2Label}
          control={control}
          rules={{ validate: validateFile }}
          noFileSelectedLabel={noFileSelectedLabel}
          helpText={allowedFileFormatsLabel}
          htmlId="extraDocument2"
          name="extraDocument2"
          accept={accept}
        />
        <ValidationError errors={errors} name="extraDocument2" />
      </div>

      <div className="form-item">
        <CheckboxWithController
          label={subscribedToNewsletterLabel}
          id="subscribedToNewsletter"
          control={control}
          rules={{}}
          name="subscribedToNewsletter"
          ControlLabel={JobApplyFormControlLabel}
        />
      </div>

      { graphqlError
      && (
        <div className="form-item">
          <ValidationError>{ graphqlError.message }</ValidationError>
        </div>
      )}

      <Button
        disabled={submitDisabled}
        className="application-submit-button"
        type="submit"
      >
        {submitLabel}
      </Button>

      {uploadProgress}
    </form>
  );
};

JobApplyForm.propTypes = jobApplyFormPropertyTypes;

export default JobApplyForm;
